import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Droplet, Truck, Recycle, ShieldCheck, Award, Clock, CheckCircle2, Phone, MapPin, Users, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

const HuilesMinérales = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Collecte d'Huiles Minérales Usagées",
    "description": "Service professionnel de collecte d'huiles minérales usagées : huiles moteurs, huiles de vidange et huiles hydrauliques",
    "provider": {
      "@type": "Organization",
      "name": "Groupe Fabre Environnement",
      "url": "https://groupefabre.com"
    },
    "serviceType": "Collecte et recyclage d'huiles usagées",
    "areaServed": {
      "@type": "Place",
      "name": "France"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <SEO 
        title="Collecte d'Huiles Minérales Usagées - Huiles Moteurs, Vidange, Hydrauliques | Groupe Fabre" 
        description="✓ Service professionnel de collecte d'huiles minérales usagées ✓ Huiles moteurs, vidange, hydrauliques ✓ Agrément ICPE ✓ 100% valorisation ✓ Devis gratuit"
        canonical="/services/huiles-minerales"
        keywords="collecte huiles minérales, huiles usagées, huiles moteurs, huiles hydrauliques, recyclage huiles, ICPE, valorisation huiles"
      />
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-fabre-green via-fabre-darkgreen to-fabre-gray text-white py-20 mt-20 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-32 -translate-x-32"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-white/20 text-white border-white/30 hover:bg-white/30">
              <Droplet className="w-4 h-4 mr-2" />
              Service Professionnel Agréé ICPE
            </Badge>
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 leading-tight">
              Collecte d'Huiles 
              <span className="text-fabre-lightgreen"> Minérales</span> Usagées
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100 leading-relaxed">
              Solutions professionnelles pour la collecte et valorisation d'huiles moteurs, 
              huiles de vidange et huiles hydrauliques
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button size="lg" className="bg-white text-fabre-green hover:bg-gray-100 font-semibold px-8">
                <Phone className="w-5 h-5 mr-2" />
                Demander un devis gratuit
              </Button>
              <Button size="lg" variant="outline" className="border-3 border-white text-white hover:bg-white hover:text-fabre-green px-8 shadow-2xl bg-white/10 backdrop-blur-sm font-semibold">
                <MapPin className="w-5 h-5 mr-2" />
                Nos sites de collecte
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="text-3xl font-bold text-fabre-lightgreen mb-2">30+</div>
                <p className="text-gray-200">Années d'expérience</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-fabre-lightgreen mb-2">100%</div>
                <p className="text-gray-200">Valorisation garantie</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-fabre-lightgreen mb-2">8</div>
                <p className="text-gray-200">Départements couverts</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            {/* Services Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-fabre-green">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-fabre-green text-xl">
                    <Droplet className="mr-3 h-7 w-7" />
                    Types d'Huiles Collectées
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    <li className="flex items-start group">
                      <CheckCircle2 className="w-5 h-5 text-fabre-green mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Huiles moteurs usagées</span>
                        <p className="text-sm text-gray-600 mt-1">Automobiles, poids lourds, engins de chantier</p>
                      </div>
                    </li>
                    <li className="flex items-start group">
                      <CheckCircle2 className="w-5 h-5 text-fabre-green mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Huiles de vidange</span>
                        <p className="text-sm text-gray-600 mt-1">Toutes viscosités et specifications</p>
                      </div>
                    </li>
                    <li className="flex items-start group">
                      <CheckCircle2 className="w-5 h-5 text-fabre-green mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Huiles hydrauliques</span>
                        <p className="text-sm text-gray-600 mt-1">Industrielles et mobiles</p>
                      </div>
                    </li>
                    <li className="flex items-start group">
                      <CheckCircle2 className="w-5 h-5 text-fabre-green mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Huiles de transmission</span>
                        <p className="text-sm text-gray-600 mt-1">Boîtes de vitesses, différentiels</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-fabre-green">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-fabre-green text-xl">
                    <Truck className="mr-3 h-7 w-7" />
                    Service de Collecte
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-fabre-green mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Collecte sur site</span>
                        <p className="text-sm text-gray-600 mt-1">Véhicules spécialisés et agréés</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-fabre-green mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Fréquence adaptée</span>
                        <p className="text-sm text-gray-600 mt-1">Planning selon vos besoins</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-fabre-green mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Contenants fournis</span>
                        <p className="text-sm text-gray-600 mt-1">Matériel propre et entretenu</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-fabre-green mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-fabre-gray">Traçabilité complète</span>
                        <p className="text-sm text-gray-600 mt-1">Documentation réglementaire</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Process Section */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-16">
              <h2 className="text-3xl font-heading font-bold text-center mb-12 text-fabre-gray">
                Notre processus de valorisation
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center group">
                  <div className="w-16 h-16 bg-fabre-green/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-fabre-green/20 transition-colors">
                    <Truck className="w-8 h-8 text-fabre-green" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">1. Collecte</h3>
                  <p className="text-gray-600">Enlèvement sur site avec matériel agréé</p>
                </div>
                
                <div className="text-center group">
                  <div className="w-16 h-16 bg-fabre-green/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-fabre-green/20 transition-colors">
                    <Recycle className="w-8 h-8 text-fabre-green" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">2. Traitement</h3>
                  <p className="text-gray-600">Régénération en centre agréé</p>
                </div>
                
                <div className="text-center group">
                  <div className="w-16 h-16 bg-fabre-green/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-fabre-green/20 transition-colors">
                    <TrendingUp className="w-8 h-8 text-fabre-green" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">3. Valorisation</h3>
                  <p className="text-gray-600">100% recyclé en nouveaux produits</p>
                </div>
              </div>
            </div>

            {/* Benefits Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-fabre-green">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-fabre-green text-xl">
                    <Recycle className="mr-3 h-7 w-7" />
                    Valorisation & Recyclage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-gray-700 mb-4">Nos huiles usagées sont intégralement valorisées :</p>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <Badge variant="outline" className="mr-3 text-fabre-green border-fabre-green">85%</Badge>
                        <span>Régénération en huiles de base</span>
                      </div>
                      <div className="flex items-center">
                        <Badge variant="outline" className="mr-3 text-fabre-green border-fabre-green">10%</Badge>
                        <span>Valorisation énergétique en cimenterie</span>
                      </div>
                      <div className="flex items-center">
                        <Badge variant="outline" className="mr-3 text-fabre-green border-fabre-green">5%</Badge>
                        <span>Recyclage en produits dérivés</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-fabre-green">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center text-fabre-green text-xl">
                    <ShieldCheck className="mr-3 h-7 w-7" />
                    Conformité Réglementaire
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-gray-700 mb-4">Service conforme aux normes :</p>
                    <ul className="space-y-3">
                      <li className="flex items-center">
                        <Award className="w-5 h-5 text-fabre-green mr-3 flex-shrink-0" />
                        <span>Agrément préfectoral ICPE</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-fabre-green mr-3 flex-shrink-0" />
                        <span>Traçabilité réglementaire</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-fabre-green mr-3 flex-shrink-0" />
                        <span>Certificats de valorisation</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-5 h-5 text-fabre-green mr-3 flex-shrink-0" />
                        <span>Respect du code de l'environnement</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-fabre-green to-fabre-darkgreen text-white rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-3xl font-heading font-bold mb-4">
                Pourquoi choisir notre service ?
              </h2>
              <p className="text-xl mb-8 text-gray-100">
                Une expertise reconnue au service de l'environnement
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                  <Clock className="w-12 h-12 text-fabre-lightgreen mx-auto mb-4" />
                  <h4 className="font-semibold text-lg mb-2">Expertise</h4>
                  <p className="text-gray-100">Plus de 30 ans d'expérience dans la collecte d'huiles usagées</p>
                </div>
                <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                  <ShieldCheck className="w-12 h-12 text-fabre-lightgreen mx-auto mb-4" />
                  <h4 className="font-semibold text-lg mb-2">Fiabilité</h4>
                  <p className="text-gray-100">Service régulier et traçabilité garantie</p>
                </div>
                <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                  <Recycle className="w-12 h-12 text-fabre-lightgreen mx-auto mb-4" />
                  <h4 className="font-semibold text-lg mb-2">Écologie</h4>
                  <p className="text-gray-100">100% de valorisation, zéro déchet en décharge</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-fabre-green hover:bg-gray-100 font-semibold px-8">
                    <Phone className="w-5 h-5 mr-2" />
                    Contactez-nous
                  </Button>
                </Link>
                <Link to="/sites">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-fabre-green px-8 shadow-xl bg-white/10 backdrop-blur-sm font-semibold">
                    <Users className="w-5 h-5 mr-2" />
                    Nos sites
                  </Button>
                </Link>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-heading font-bold text-center mb-8 text-fabre-gray">
                Questions fréquentes
              </h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-fabre-green pl-6">
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">Quels types d'huiles collectez-vous ?</h3>
                  <p className="text-gray-600">Nous collectons toutes les huiles minérales usagées : huiles moteurs, huiles hydrauliques, huiles de transmission, huiles de coupe, etc.</p>
                </div>
                
                <div className="border-l-4 border-fabre-green pl-6">
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">Quelle est la fréquence de collecte ?</h3>
                  <p className="text-gray-600">La fréquence est adaptée à vos besoins : hebdomadaire, mensuelle ou à la demande selon votre volume de production.</p>
                </div>
                
                <div className="border-l-4 border-fabre-green pl-6">
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">Fournissez-vous les contenants ?</h3>
                  <p className="text-gray-600">Oui, nous fournissons tous les contenants nécessaires (cuves, fûts, bacs) et assurons leur entretien.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default HuilesMinérales;